import React, { useState } from "react";
import {
  MdArrowBack,
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdRemoveCircle,
  MdAddCircle,
} from "react-icons/md";
import { Link, useParams, useNavigate } from "react-router-dom";
import useTdsRegimeComparison from "../../../../hooks/useTdsRegimeComparison";
import PrimaryLoader from "../../../../components/common/PrimaryLoader";

const InvestmentDeclarationCTR = () => {
  const { company_employee_id } = useParams();
  const navigate = useNavigate();
  const {
    tdsData,
    isLoading,
    selectedRegime,
    setSelectedRegime,
    handleSubmit,
    isSubmitting
  } = useTdsRegimeComparison();

  // track open/collapsed state for rental rows
  const [openRentalRows, setOpenRentalRows] = useState({});
  const toggleRentalRow = (idx) => {
    setOpenRentalRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <PrimaryLoader />
      </div>
    );
  }

  const declarationData = tdsData;

  // Get total tax values from API response and format currency
  const oldTaxValue = declarationData?.main_submission_data?.employee_regime_selection?.total_tax_calculated?.old_regime;
  const newTaxValue = declarationData?.main_submission_data?.employee_regime_selection?.total_tax_calculated?.new_regime;
  const formatTax = (val) => {
    if (val == null || val === "") return "N/A";
    const num = parseFloat(val);
    if (isNaN(num)) return "N/A";
    return `₹${num.toLocaleString()}`;
  };

  // Helper function to calculate months difference
  const calculateMonthsDifference = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const yearDiff = to.getFullYear() - from.getFullYear();
    const monthDiff = to.getMonth() - from.getMonth();
    return yearDiff * 12 + monthDiff + 1;
  };

  // Transform data for Old Tax Regime (similar to ITDeclaration)
  const getOldRegimeData = () => {
    const particulars = [];

    // Add House Rent if exists
    if (declarationData?.house_rent?.length > 0) {
      const houseRentItems = declarationData.house_rent.map((rent) => {
        const months = calculateMonthsDifference(rent.from_date, rent.to_date);
        const totalAmount = parseFloat(rent.rent_amount) * months;

        // Format dates
        const fromDate = new Date(rent.from_date).toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });
        const toDate = new Date(rent.to_date).toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });

        return {
          period: `${fromDate} - ${toDate}`,
          address: rent.address,
          landlordName: rent.landlord_name,
          landlordPan: rent.landlord_pan,
          monthlyRent: parseFloat(rent.rent_amount),
          declaredAmount: `₹${totalAmount.toLocaleString()}`,
          isMetro: rent.is_metro === "1",
        };
      });

      particulars.push({
        section: "House Rent Paid Details",
        items: houseRentItems,
        isHouseRent: true,
      });
    }

    // Add Other Income if exists
    if (declarationData?.other_source_income) {
      const otherIncome = declarationData.other_source_income;
      const incomeItems = [];

      if (otherIncome.income_from_other_sources && parseFloat(otherIncome.income_from_other_sources) > 0) {
        incomeItems.push({
          name: "Income from other sources",
          declaredAmount: `₹${parseFloat(otherIncome.income_from_other_sources).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from Savings Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_savings).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
        incomeItems.push({
          name: "Interest Earned from Fixed Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_fixed_deposits).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from National Savings Certificates",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_national_savings).toLocaleString()}`,
        });
      }

      if (incomeItems.length > 0) {
        particulars.push({
          section: "Other Income",
          items: incomeItems,
          isOtherIncome: true,
        });
      }
    }

    // Add 80C investments if exists
    if (declarationData?.investments_80C?.length > 0) {
      const items = declarationData.investments_80C.map((investment) => ({
        name: investment.label,
        declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
      }));

      particulars.push({
        section: "80C Investments",
        items,
      });
    }

    // Add 80D exemptions if exists
    if (declarationData?.exemptions_80D?.length > 0) {
      const items = declarationData.exemptions_80D.map((exemption) => ({
        name: exemption.label,
        declaredAmount: `₹${parseFloat(exemption.amount).toLocaleString()}`,
      }));

      particulars.push({
        section: "80D Exemptions",
        items,
      });
    }

    // Add Other Investments if exists
    if (declarationData?.other_investments?.length > 0) {
      const items = declarationData.other_investments.map((investment) => ({
        name: investment.label,
        declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
      }));

      particulars.push({
        section: "Other Investments & Exemptions",
        items,
      });
    }

    // Add Rental Income / Net Income-Loss from House Property (Old regime)
    if (declarationData?.rental_income?.length > 0) {
      const rentalIncomeArray = declarationData.rental_income;
      const baseTotal = rentalIncomeArray.reduce(
        (sum, it) => sum + (parseFloat(it.net_income_loss || 0) || 0),
        0
      );
      const homeLoanMain = declarationData?.home_loan || null;
      const mainHomeLoanInterest = parseFloat(homeLoanMain?.interest_amount || 0);
      const displayedTotal = baseTotal - mainHomeLoanInterest; // Zoho behaviour for old regime
      const homeLoanInterestDisplay =
        mainHomeLoanInterest > 0 ? `₹${mainHomeLoanInterest.toLocaleString()}` : null;

      const rentalItems = rentalIncomeArray.map((it, idx) => ({
        key: it.rental_income_id || idx,
        title: `Let Out Property - ${idx + 1}`,
        annual_rent_received: parseFloat(it.annual_rent_received || 0),
        municipal_taxes_paid: parseFloat(it.municipal_taxes_paid || 0),
        net_annual_value: parseFloat(it.net_annual_value || 0),
        standard_deduction: parseFloat(it.standard_deduction || 0),
        repaying_home_loan: it.repaying_home_loan === "1" || it.repaying_home_loan === 1 || it.repaying_home_loan === true,
        repaying_home_loan_data: {
          principal_amount: parseFloat(it.principal_amount || 0),
          interest_amount: parseFloat(it.interest_amount || 0),
          name_of_lender: it.name_of_lender || "",
          lender_pan: it.lender_pan || "",
        },
        net_income_loss: parseFloat(it.net_income_loss || 0),
        amountDisplay: `₹${(parseFloat(it.net_income_loss || 0)).toLocaleString()}`,
      }));

      particulars.push({
        section: "Net Income / Loss from House Property",
        isRentalIncome: true,
        summary: {
          totalNetFromLetOut: displayedTotal,
          baseTotal,
          homeLoanInterestDisplay,
          homeLoanLender: homeLoanMain?.name_of_lender || "",
          homeLoanLenderPan: homeLoanMain?.lender_pan || "",
        },
        items: rentalItems,
      });
    }

    return particulars;
  };

  // Transform data for New Tax Regime (only Other Income)
  const getNewRegimeData = () => {
    const particulars = [];

    // Only add Other Income for new regime
    if (declarationData?.other_source_income) {
      const otherIncome = declarationData.other_source_income;
      const incomeItems = [];

      if (otherIncome.income_from_other_sources && parseFloat(otherIncome.income_from_other_sources) > 0) {
        incomeItems.push({
          name: "Income from other sources",
          declaredAmount: `₹${parseFloat(otherIncome.income_from_other_sources).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from Savings Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_savings).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
        incomeItems.push({
          name: "Interest Earned from Fixed Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_fixed_deposits).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from National Savings Certificates",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_national_savings).toLocaleString()}`,
        });
      }

      if (incomeItems.length > 0) {
        particulars.push({
          section: "Other Income",
          items: incomeItems,
          isOtherIncome: true,
        });
      }
    }

    // Add Rental Income / Net Income-Loss from House Property (New regime - simpler)
    if (declarationData?.rental_income?.length > 0) {
      const rentalIncomeArray = declarationData.rental_income;
      const baseTotal = rentalIncomeArray.reduce(
        (sum, it) => sum + (parseFloat(it.net_income_loss || 0) || 0),
        0
      );
      const rentalItems = rentalIncomeArray.map((it, idx) => ({
        key: it.rental_income_id || idx,
        title: `Let Out Property - ${idx + 1}`,
        annual_rent_received: parseFloat(it.annual_rent_received || 0),
        municipal_taxes_paid: parseFloat(it.municipal_taxes_paid || 0),
        net_annual_value: parseFloat(it.net_annual_value || 0),
        standard_deduction: parseFloat(it.standard_deduction || 0),
        repaying_home_loan: it.repaying_home_loan === "1" || it.repaying_home_loan === 1 || it.repaying_home_loan === true,
        repaying_home_loan_data: {
          principal_amount: parseFloat(it.principal_amount || 0),
          interest_amount: parseFloat(it.interest_amount || 0),
          name_of_lender: it.name_of_lender || "",
          lender_pan: it.lender_pan || "",
        },
        net_income_loss: parseFloat(it.net_income_loss || 0),
        amountDisplay: `₹${(parseFloat(it.net_income_loss || 0)).toLocaleString()}`,
      }));

      particulars.push({
        section: "Net Income / Loss from House Property",
        isRentalIncome: true,
        summary: {
          totalNetFromLetOut: baseTotal,
          baseTotal,
          homeLoanInterestDisplay: null,
          homeLoanLender: "",
          homeLoanLenderPan: "",
        },
        items: rentalItems,
      });
    }

    return particulars;
  };

  const currentParticulars = selectedRegime === "old" ? getOldRegimeData() : getNewRegimeData();

  const handleCancel = () => {
    navigate(`/employee-details/${company_employee_id}/investments-and-proofs`);
  };

  return (
    <div className="w-[75%] p-6 mt-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Compare Tax Details
        </h1>
      </div>

      {/* Tax Regime Selection Cards */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Old Tax Regime Card */}
        <div
          className={`relative bg-white p-6 rounded-lg border transition-all cursor-pointer flex flex-col justify-center
            ${
              selectedRegime === "old"
                ? "border-blue-500"
                : "border-[#E3E6EA] hover:shadow-md"
            }
          `}
          onClick={() => setSelectedRegime("old")}
        >
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center h-full">
              {selectedRegime === "old" ? (
                <MdCheckCircle className="w-5 h-5 text-blue-600" />
              ) : (
                <MdRadioButtonUnchecked className="w-5 h-5 text-gray-400" />
              )}
            </span>
            <div className="flex flex-col">
              <span className="text-[17px] font-semibold text-gray-900">
                Old Tax Regime
              </span>
              <span className="text-[15px] text-gray-500 font-medium mt-1">
                Total Tax :{" "}
                <span className="text-[15px] text-gray-900 font-medium">
                  {formatTax(oldTaxValue)}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* New Tax Regime Card */}
        <div
          className={`relative bg-white p-6 rounded-lg border transition-all cursor-pointer flex flex-col justify-center
            ${
              selectedRegime === "new"
                ? "border-blue-500"
                : "border-[#E3E6EA] hover:shadow-md"
            }
          `}
          onClick={() => setSelectedRegime("new")}
        >
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center h-full">
              {selectedRegime === "new" ? (
                <MdCheckCircle className="w-5 h-5 text-blue-600" />
              ) : (
                <MdRadioButtonUnchecked className="w-5 h-5 text-gray-400" />
              )}
            </span>
            <div className="flex flex-col">
              <span className="text-[17px] font-semibold text-gray-900">
                New Tax Regime
              </span>
              <span className="text-[15px] text-gray-500 font-medium mt-1">
                Total Tax :{" "}
                <span className="text-[15px] text-gray-900 font-medium">
                  {formatTax(newTaxValue)}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Particulars Table */}
      <div className="bg-white mb-8">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex">
            <div className="w-3/5">
              <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                PARTICULARS
              </span>
            </div>
            <div className="w-2/5 text-right">
              <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                DECLARED AMOUNT
              </span>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="divide-y divide-gray-200">
          {currentParticulars.length > 0 ? (
            currentParticulars.map((section, sectionIndex) => (
              <div key={sectionIndex} className="p-6">
                {/* Section Header */}
                <div className="flex mb-4">
                  <div className="w-3/5">
                    <span className="text-base font-semibold text-gray-900">
                      {section.section}
                    </span>
                  </div>
                </div>

                {/* Section Items */}
                <div>
                  {section.isRentalIncome ? (
                    // Rental Income summary + per-property rows
                    <div>
                      <div className="flex mb-2">
                        <div className="w-3/5 text-gray-700 font-medium">
                          Total Income / Loss from House Property
                        </div>
                        <div className="w-2/5 text-right text-gray-900 font-semibold">
                          {section.summary.totalNetFromLetOut ? `₹${section.summary.totalNetFromLetOut.toLocaleString()}` : "0"}
                        </div>
                      </div>

                      {selectedRegime === "old" && section.summary.homeLoanInterestDisplay && (
                        <div className="flex mb-3">
                          <div className="w-3/5 text-gray-700">
                            Interest Paid on Home Loan
                            {section.summary.homeLoanLender && (
                              <div className="text-xs text-gray-500 mt-1">
                                Lender: {section.summary.homeLoanLender} &nbsp; | &nbsp; PAN: {section.summary.homeLoanLenderPan}
                              </div>
                            )}
                          </div>
                          <div className="w-2/5 text-right text-gray-900 font-semibold">
                            {section.summary.homeLoanInterestDisplay}
                          </div>
                        </div>
                      )}

                      {section.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => toggleRentalRow(itemIndex)}
                                className="flex items-center gap-2 text-sm text-gray-700"
                                aria-expanded={!!openRentalRows[itemIndex]}
                              >
                                {openRentalRows[itemIndex] ? (
                                  <MdRemoveCircle className="text-red-500 w-5 h-5" />
                                ) : (
                                  <MdAddCircle className="text-blue-500 w-5 h-5" />
                                )}
                                <span className="font-medium text-gray-900">{item.title}</span>
                              </button>
                            </div>
                            <div className="text-gray-800 text-sm font-semibold text-right">
                              {item.amountDisplay}
                            </div>
                          </div>

                          {openRentalRows[itemIndex] && (
                            <div className="pl-4 border-l-2 border-gray-100 mt-3 space-y-2">
                              <div className="flex justify-between text-sm text-gray-700">
                                <div>Annual Rent Received</div>
                                <div>₹{item.annual_rent_received.toLocaleString()}</div>
                              </div>
                              <div className="flex justify-between text-sm text-gray-700">
                                <div>Municipal Taxes Paid</div>
                                <div>₹{item.municipal_taxes_paid.toLocaleString()}</div>
                              </div>
                              <div className="flex justify-between text-sm text-gray-700">
                                <div>Net Annual Value</div>
                                <div>₹{item.net_annual_value.toLocaleString()}</div>
                              </div>
                              <div className="flex justify-between text-sm text-gray-700">
                                <div>Standard Deduction (@ 30% of Net Annual Value)</div>
                                <div>₹{item.standard_deduction.toLocaleString()}</div>
                              </div>

                              {item.repaying_home_loan && (
                                <>
                                  <div className="flex justify-between text-sm text-gray-700">
                                    <div>Interest Paid on Home Loan</div>
                                    <div>₹{(item.repaying_home_loan_data.interest_amount || 0).toLocaleString()}</div>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Lender: {item.repaying_home_loan_data.name_of_lender || "-"} &nbsp; | &nbsp; PAN: {item.repaying_home_loan_data.lender_pan || "-"}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : section.isHouseRent ? (
                    // House Rent specific layout - matching ITDeclaration
                    section.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="pb-4 border-b border-gray-100 last:border-b-0">
                        <div className="flex">
                          <div className="w-3/5">
                            <span className="text-sm text-gray-700 font-medium">
                              {item.period}
                            </span>
                          </div>
                          <div className="w-2/5 text-right">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.declaredAmount}
                            </span>
                            <div className="text-gray-500 text-xs mt-1">
                              (₹{item.monthlyRent.toLocaleString()} / month)
                            </div>
                          </div>
                        </div>

                        {/* Additional details */}
                        <div className="text-gray-600 text-xs mt-3">
                          <div className="mb-1">
                            <span className="font-medium">{item.address}</span>
                          </div>
                          <div className="mb-1">
                            <span className="text-gray-500">Landlord Name : </span>
                            <span className="font-medium">{item.landlordName}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">PAN : </span>
                            <span className="font-medium">{item.landlordPan}</span>
                            {item.isMetro && (
                              <span className="ml-2 text-blue-600">• Metro</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : section.isOtherIncome ? (
                    // Other Income specific layout - matching ITDeclaration
                    section.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="py-2">
                        <div className="flex">
                          <div className="w-3/5">
                            <span className="text-sm text-gray-700">
                              {item.name}
                            </span>
                          </div>
                          <div className="w-2/5 text-right">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.declaredAmount}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Regular items layout - matching ITDeclaration
                    section.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="py-2">
                        <div className="flex">
                          <div className="w-3/5">
                            <span className="text-sm text-gray-700">
                              {item.name}
                            </span>
                          </div>
                          <div className="w-2/5 text-right">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.declaredAmount}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              No particulars available for this tax regime
            </div>
          )}
        </div>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">i</span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              POI will also be processed based on the {selectedRegime === "old" ? "Old Tax Regime" : "New Tax Regime"}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          <button
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>

        <Link
          to={`/employee-details/${company_employee_id}/investments-and-proofs`}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
        >
          <MdArrowBack className="w-4 h-4" />
          Edit Declaration
        </Link>
      </div>
    </div>
  );
};

export default InvestmentDeclarationCTR;
